import React from 'react'
import { Check, X } from 'lucide-react'
import { useCurrency } from '../../../contexts/CurrencyContext'
import type { TradeItem } from './types'
import {
  buildLinePriceUpdate,
  readLineMarketCents,
  readLinePricing,
  type LinePriceEdit,
  type LinePriceUpdate,
} from './customerPricing'
import type { BulkFloorUsd } from '../../../utils/tradeRules'
import styles from '../CreateTransaction.module.css'

export type TradeLinePriceEditorProps = {
  item: TradeItem
  formatPrice: (cents: number) => string
  /** The trade's "Customer % of Market" ('' when unset) — what "Follow global" values a line at. */
  globalCustomerPercent: number | ''
  /** The store's bulk floor in USD cents, for the "Follow global" preview (the server re-prices). */
  bulkFloorUsd?: BulkFloorUsd | null
  /** True while other edits to this trade are still saving. */
  disabled?: boolean
  /** Sends the update; resolves false when it failed (the page shows the error). */
  onSave: (update: LinePriceUpdate) => Promise<boolean>
  /** The editor opened or closed: the page holds back a refresh from another device while it is open. */
  onEditingChange?: (editing: boolean) => void
}

/**
 * A trade line's total, tappable to reprice the line in place (each, not total).
 *
 * Store lines take a price. Customer lines take a price or a % of the market value they were added
 * at; either locks the line from the global Customer % of Market, as in the add-item modal, and a
 * locked line can be handed back to the global %. Prices are typed in the display currency and
 * stored as base (USD) cents, converted the same way the add-item modal does.
 */
export function TradeLinePriceEditor({
  item,
  formatPrice,
  globalCustomerPercent,
  bulkFloorUsd = null,
  disabled = false,
  onSave,
  onEditingChange,
}: TradeLinePriceEditorProps) {
  const { currency, tryConvertPrice, baseCurrency } = useCurrency()
  const isCustomer = item.side === 'customer'
  const pricing = readLinePricing(item.metadata)
  const marketCents = readLineMarketCents(item.metadata)
  const isLocked = isCustomer && pricing.manualOverride === true
  const globalPercent = typeof globalCustomerPercent === 'number' ? globalCustomerPercent : null

  const [editing, setEditing] = React.useState(false)
  const [draft, setDraft] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [inputError, setInputError] = React.useState<string | null>(null)
  const onEditingChangeRef = React.useRef(onEditingChange)
  onEditingChangeRef.current = onEditingChange

  // Open → closed (or the line went away mid-edit) is reported once each way.
  React.useEffect(() => {
    if (!editing) return
    onEditingChangeRef.current?.(true)
    return () => onEditingChangeRef.current?.(false)
  }, [editing])

  const amountDraft = (): string => {
    const display = tryConvertPrice(item.valueCents, baseCurrency, currency)
    return display === null ? '' : (display / 100).toFixed(2)
  }

  const startEditing = () => {
    setDraft(amountDraft())
    setInputError(null)
    setEditing(true)
  }

  const cancel = () => {
    setEditing(false)
    setInputError(null)
  }

  const save = async (edit: LinePriceEdit) => {
    const update = buildLinePriceUpdate(item, edit)
    if (!update) {
      setInputError('This card has no market price to take a % of.')
      return
    }
    setSaving(true)
    const ok = await onSave(update)
    setSaving(false)
    if (ok) setEditing(false)
  }

  const commit = () => {
    const value = parseFloat(draft)
    if (!Number.isFinite(value) || value <= 0) {
      setInputError('Enter a price greater than 0.')
      return
    }
    const usdCents = tryConvertPrice(Math.round(value * 100), currency, baseCurrency)
    if (usdCents === null) {
      // No rate yet: refuse rather than store the display amount as USD.
      setInputError('Exchange rates are still loading — try again in a moment.')
      return
    }
    void save({ kind: 'amount', valueCents: usdCents })
  }

  if (!editing) {
    return (
      <div className={styles.itemValue}>
        <button
          type="button"
          className={styles.linePriceBtn}
          onClick={startEditing}
          disabled={disabled}
          title="Edit price"
          aria-label={`Edit price for ${item.itemName}`}
        >
          {formatPrice(item.totalValueCents)}
        </button>
      </div>
    )
  }

  return (
    <div className={`${styles.itemValue} ${styles.linePriceEditor}`}>
      <form
        className={styles.linePriceForm}
        onSubmit={(e) => {
          e.preventDefault()
          commit()
        }}
      >
        <input
          type="number"
          inputMode="decimal"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value)
            setInputError(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel()
          }}
          step="0.01"
          min="0"
          autoFocus
          className={styles.linePriceInput}
          aria-label={`Price each for ${item.itemName} in ${currency}`}
        />
        <span className={styles.linePriceUnit}>each</span>
        <button type="submit" className={styles.linePriceIconBtn} disabled={saving} aria-label="Save price">
          <Check size={14} aria-hidden />
        </button>
        <button type="button" className={styles.linePriceIconBtn} onClick={cancel} disabled={saving} aria-label="Cancel price edit">
          <X size={14} aria-hidden />
        </button>
      </form>
      {isCustomer && !isLocked && (
        <span className={styles.linePriceHint}>The trade&rsquo;s % won&rsquo;t change this card after saving.</span>
      )}
      {isLocked && marketCents > 0 && (
        <button
          type="button"
          className={styles.followGlobalBtn}
          disabled={saving}
          onClick={() => void save({ kind: 'followGlobal', globalPercent, floor: bulkFloorUsd })}
        >
          Follow the trade{globalPercent !== null ? ` (${globalPercent}%)` : ''} instead
        </button>
      )}
      {inputError && (
        <span className={styles.linePriceError} role="alert">
          {inputError}
        </span>
      )}
    </div>
  )
}
